import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Mail, Phone, MapPin, Car, Package, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { createAppointment } from '../api/api';

const servicesList = [
  { name: "Mini Detail", duration: "Approx 2 hours" },
  { name: "Essential Detail", duration: "Approx 2.5-3 hours" },
  { name: "Interior Reset Package", duration: "2-3 hours" },
  { name: "Signature Detail Package", duration: "4-6 hours" },
  { name: "Paint Enhancement & Correction", duration: "6-8 hours" },
  { name: "Clay and Seal Package", duration: "2-3 hours" },
  { name: "Maintenance Package", duration: "Biweekly or Monthly" }
];

export const Booking = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    vehicleType: '',
    location: '',
    preferredDate: '',
    preferredTime: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.phone || !formData.service || !formData.location || !formData.preferredDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    
    try {
      // Create appointment via API
      await createAppointment(formData);
      
      toast.success('Appointment request submitted successfully!');
      
      // Redirect to confirmation page with booking details
      navigate('/appointment-confirmation', {
        state: { bookingDetails: formData }
      });
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error('Failed to submit appointment. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Book Your Appointment</h1>
            <p className="text-xl text-gray-600">Fill out the form below and we'll get back to you shortly</p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="border-b bg-gray-50">
              <CardTitle className="text-2xl">Appointment Details</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Information
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(909) 555-0123"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Service Details
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="service">Select Service *</Label>
                    <Select value={formData.service} onValueChange={(value) => handleChange('service', value)} required>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Choose a service package" />
                      </SelectTrigger>
                      <SelectContent>
                        {servicesList.map((service, idx) => (
                          <SelectItem key={idx} value={service.name}>
                            {service.name} ({service.duration})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType">Vehicle Type</Label>
                    <Select value={formData.vehicleType} onValueChange={(value) => handleChange('vehicleType', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="luxury">Luxury Vehicle</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location & Schedule */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Location & Schedule
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="location">Service Location *</Label>
                    <Input
                      id="location"
                      type="text"
                      placeholder="Full address in Redlands or surrounding area"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      required
                      className="h-12"
                    />
                    <p className="text-sm text-gray-500">We service Redlands and surrounding areas</p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="preferredDate">Preferred Date *</Label>
                      <Input
                        id="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={(e) => handleChange('preferredDate', e.target.value)}
                        required
                        className="h-12"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="preferredTime">Preferred Time</Label>
                      <Select value={formData.preferredTime} onValueChange={(value) => handleChange('preferredTime', value)}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7am-9am">7:00 AM - 9:00 AM</SelectItem>
                          <SelectItem value="9am-11am">9:00 AM - 11:00 AM</SelectItem>
                          <SelectItem value="11am-1pm">11:00 AM - 1:00 PM</SelectItem>
                          <SelectItem value="1pm-3pm">1:00 PM - 3:00 PM</SelectItem>
                          <SelectItem value="3pm-5pm">3:00 PM - 5:00 PM</SelectItem>
                          <SelectItem value="5pm-7pm">5:00 PM - 7:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any special requests or additional information..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full cta-button text-lg h-14"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Appointment Request'}
                  </Button>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    We'll contact you within 24 hours to confirm your appointment
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Booking;
